import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button, Card, CardContent, CircularProgress, Popover } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';

import AppLookupChip from '@/features/apps/components/AppLookupChip';
import { useGetFocusAreaAllResponses } from '@/features/engagement/engagement.services';
import { FocusAreaDueResponseCountDto, SignalResponseDTO } from '@/features/engagement/engagement.types';

interface IProps {
  anchorEl: HTMLButtonElement | null;
  focusArea: FocusAreaDueResponseCountDto | null;
  onClose: () => void;
  onOpenSignalResponse: (focusArea: FocusAreaDueResponseCountDto, response: SignalResponseDTO) => void;
}

const FocusAreaAllResponsesPopover = ({ anchorEl, focusArea, onClose, onOpenSignalResponse }: IProps) => {
  const open = Boolean(anchorEl) && !!focusArea;
  const { data, isFetching } = useGetFocusAreaAllResponses(focusArea?.id ?? null, open);

  return (
    <Popover
      id={open ? 'focus-area-all-responses-popover' : undefined}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
    >
      <Card sx={{ width: 700, maxWidth: '100%', maxHeight: 600, overflow: 'auto' }}>
        <CardContent>
          {isFetching ? (
            <CircularProgress size={22} />
          ) : (
            <div className='flex flex-col gap-3'>
              <Button variant='text' sx={{ alignSelf: 'flex-start', color: 'inherit', fontSize: 12 }} onClick={onClose}>
                <ArrowBackIcon />
                Back
              </Button>

              <div className='flex flex-col gap-1'>
                {focusArea ? (
                  <AppLookupChip lookup={focusArea} isSelected={true} />
                ) : (
                  <span className='text-sm font-semibold text-[#111827]'>{data?.focusAreaTitle}</span>
                )}
              </div>

              {(data?.strategies || []).length > 0 ? (
                data?.strategies.map(strategy => (
                  <div key={strategy.strategyId} className='rounded-md border border-[#E5E7EB] p-3'>
                    <div className='mb-2 border-b border-[#F3F4F6] pb-2'>
                      <span className='text-xs font-semibold uppercase tracking-wide text-[#6B7280]'>
                        {strategy.strategyName}
                      </span>
                    </div>

                    <div className='flex flex-col gap-2'>
                      {strategy.signals.map(signal => (
                        <div key={signal.questionId} className='rounded-md bg-[#F9FAFB] p-2'>
                          <div className='mb-2'>
                            <span className='text-xs font-medium text-[#374151]'>{signal.signal}</span>
                          </div>
                          <div className='flex flex-wrap gap-2'>
                            {signal.timeSlots.map(timeSlot => (
                              <button
                                key={timeSlot.progressResponseId}
                                type='button'
                                onClick={() => {
                                  if (!focusArea) return;
                                  onOpenSignalResponse(focusArea, timeSlot);
                                }}
                                className='rounded-full border border-[#CC4E00] bg-white px-3 py-1 text-xs text-[#CC4E00] transition hover:bg-[#FFF4ED]'
                              >
                                {dayjs(timeSlot.scheduledPeriod).format('MMM-YYYY')}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <span className='text-sm text-[#6B7280]'>No due responses found.</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </Popover>
  );
};

export default FocusAreaAllResponsesPopover;
